import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Flame, Shield, TrendingUp, Award, ArrowRight, CheckCircle, ArrowDown, Thermometer, Droplets, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import UnifiedContactModal from './UnifiedContactModal';
import SulfurBurnerTwoCTA from './SulfurBurnerTwoCTA';

const SulfurBurners = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Consistent Water Chemistry',
      description: 'Many growers report yield and quality improvements when pH and scaling risks are controlled through inline water management'
    },
    {
      icon: Shield,
      title: 'Reduce System Clogging',
      description: 'Neutralize bicarbonates to prevent scaling in emitters and lines, keeping your irrigation system running predictably'
    },
    {
      icon: Award,
      title: 'Superior Quality',
      description: 'Built with premium acid-resistant materials for long-lasting performance in harsh agricultural conditions'
    }
  ];

  const comparisons = [
    {
      feature: 'Materials',
      ctc: 'Advanced acid-resistant alloys',
      others: 'Standard materials'
    },
    {
      feature: 'Build Quality',
      ctc: 'Precision manufacturing',
      others: 'Outdated'
    },
    {
      feature: 'Automation',
      ctc: 'Superior automated controls',
      others: 'Complicated & Unreliable'
    },
    {
      feature: 'Components',
      ctc: 'Premium grade parts',
      others: 'Basic components'
    },
    {
      feature: 'Support',
      ctc: 'Dedicated Expert Service',
      others: 'Limited support'
    }
  ];

  return (
    <section id="sulfur-burners" className={`${isMobile ? 'py-10' : 'py-20'} bg-white`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Flame size={isMobile ? 14 : 16} />
            <span>Industries Most Reliable</span>
          </div>
          <Link to="/sulfur-burners">
            <h2 className={`${isMobile ? 'text-3xl' : 'text-4xl lg:text-5xl'} font-bold text-gray-900 ${isMobile ? 'mb-3' : 'mb-6'} hover:text-ctc-blue transition-colors cursor-pointer`}>
              Sulfur Burners
            </h2>
          </Link>
          <p className={`${isMobile ? 'text-base' : 'text-xl'} text-gray-600 max-w-3xl mx-auto ${isMobile ? 'mb-3' : 'mb-4'}`}>
            Inline irrigation water chemistry management (pH + bicarbonates) for predictable operation and cleaner systems.
          </p>
          <p className={`${isMobile ? 'text-sm' : 'text-base'} text-gray-500 italic max-w-3xl mx-auto ${isMobile ? 'mb-4' : ''}`}>
            Built on decades of field experience. Used by growers and water managers across a wide range of crops and regions.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className={`bg-gradient-to-br from-ctc-silver-light via-white to-ctc-silver rounded-2xl ${isMobile ? 'p-4' : 'p-8'} shadow-xl`}>
              <img
                src="/bulk_sulfur_burners copy.png"
                alt="CTC Waterworks sulfur burner systems in manufacturing facility"
                className={`w-full ${isMobile ? 'h-48' : 'h-80'} object-cover rounded-xl shadow-lg`}
              />
            </div>
            
            {/* Feature Badge */}
            <div className={`absolute -bottom-4 -right-4 bg-white rounded-xl shadow-xl ${isMobile ? 'p-2' : 'p-4'} border border-ctc-silver`}>
              <div className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-ctc-blue`}>99.9%</div>
              <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>Burn Efficiency</div>
            </div>
          </motion.div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className={isMobile ? 'space-y-3' : ''}>
              <h3 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900 ${isMobile ? 'mb-2' : 'mb-4'}`}>
                Why Choose CTC Sulfur Burners?
              </h3>
              <p className={`text-gray-600 ${isMobile ? 'mb-3 text-sm' : 'mb-6'}`}>
                Our sulfur burners deliver consistent, controlled sulfur dioxide production
                for inline irrigation water chemistry management (pH + bicarbonates).
              </p>
            </div>

            <div className={`${isMobile ? 'space-y-3' : 'space-y-6'}`}>
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start space-x-4"
                >
                  <div className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} bg-gradient-to-br from-ctc-blue-light to-ctc-blue rounded-lg flex items-center justify-center flex-shrink-0 shadow-md`}>
                    <benefit.icon className="text-white" size={isMobile ? 20 : 24} />
                  </div>
                  <div>
                    <h4 className={`${isMobile ? 'text-base' : 'text-xl'} font-semibold text-gray-900 ${isMobile ? 'mb-1' : 'mb-2'}`}>
                      {benefit.title}
                    </h4>
                    <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Competitive Comparison - Compact */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`bg-gradient-to-br from-ctc-silver-light to-white rounded-2xl ${isMobile ? 'p-4' : 'p-6'} ${isMobile ? 'mb-8' : 'mb-12'} shadow-lg border border-ctc-silver`}
        >
          <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-gray-900 ${isMobile ? 'mb-2' : 'mb-4'} text-center`}>
            CTC vs The Other Guys
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ctc-silver">
                  <th className="text-left py-2 px-3 text-gray-700 font-medium">Feature</th>
                  <th className="text-center py-2 px-3 text-ctc-blue font-bold">CTC</th>
                  <th className="text-center py-2 px-3 text-gray-500 font-medium">The Other Guys</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((comparison, index) => (
                  <tr
                    key={index}
                    className="border-b border-ctc-silver/30 hover:bg-ctc-blue/5 transition-colors"
                  >
                    <td className="py-2 px-3 font-medium text-gray-900">
                      {comparison.feature}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <CheckCircle className="text-green-500" size={16} />
                        <span className="text-gray-700 text-xs">{comparison.ctc}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <span className="text-red-500">❌</span>
                        <span className="text-gray-600 text-xs">{comparison.others}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Technical Specs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${isMobile ? 'mb-8' : 'mb-12'}`}
        >
          <div className={`bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-xl ${isMobile ? 'p-4' : 'p-8'} border border-ctc-blue/20`}>
            <h3 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 ${isMobile ? 'mb-3' : 'mb-6'}`}>Key Features</h3>
            <div className="space-y-4">
              {[
                'Automated sulfur feed system',
                'Corrosion-resistant construction',
                'Easy maintenance access',
                'Multiple capacity options',
                'Remote monitoring capability'
              ].map((feature, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="text-green-500" size={isMobile ? 16 : 20} />
                  <span className={`text-gray-700 ${isMobile ? 'text-sm' : ''}`}>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`bg-gradient-to-br from-ctc-silver-light to-white rounded-xl ${isMobile ? 'p-4' : 'p-8'} border border-ctc-silver shadow-md`}>
            <h3 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 ${isMobile ? 'mb-3' : 'mb-6'}`}>Applications</h3>
            <div className="space-y-4">
              {[
                'Vineyard irrigation water management',
                'Greenhouse operations',
                'Row crop irrigation',
                'Programs with alkaline water',
                'Hydroponic and controlled environment'
              ].map((application, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className={`${isMobile ? 'w-1.5 h-1.5' : 'w-2 h-2'} bg-ctc-blue rounded-full`}></div>
                  <span className={`text-gray-700 ${isMobile ? 'text-sm' : ''}`}>{application}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`bg-gradient-to-r from-ctc-blue via-ctc-blue-dark to-black rounded-2xl ${isMobile ? 'p-6' : 'p-12'} shadow-2xl`}
        >
          <div className="text-center text-white mb-8">
            <h3 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold ${isMobile ? 'mb-2' : 'mb-4'}`}>
              Ready to Get Started?
            </h3>
            <p className={`${isMobile ? 'text-base' : 'text-xl'} opacity-90`}>
              Choose your path: quick sizing or detailed chemistry review
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6">
              <SulfurBurnerTwoCTA layout="stacked" size={isMobile ? 'default' : 'large'} />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Unified Contact Modal */}
      <UnifiedContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)}
        productType="sulfur-burners"
        formSource="product_quote"
      />
    </section>
  );
};

export default SulfurBurners;